<template>
  <div :class="appClasses" @keydown.esc="closeSlideins()">
    <Loading v-if="$store.getters.appIsLoading" />
    <div v-if="!$store.getters.appIsLoading" :class="layoutClasses">
      <AppHeader />
      <div class="layout__content">
        <router-view />
      </div>
      <BottomNav v-if="$store.getters.appInitialized" />
    </div>
    <MenuSlidein />
    <div class="slidein-overlay" @click="closeSlideins()"></div>
    <NotificationsRequestPopup />
    <BasePricePopup v-if="$store.getters.showBasePricePopup" />
    <NewVersion :visible="updateAvailable" />
  </div>
</template>

<script>
  import Loading from '@/components/Loading'
  import BottomNav from '@/components/BottomNav'
  import NewVersion from '@/components/NewVersion'
  import Cookie from 'js-cookie'
  import MenuSlidein from '@/components/slidein/MenuSlidein'
  import NotificationsRequestPopup from '@/components/NotificationsRequestPopup'
  import BasePricePopup from '@/components/BasePricePopup'
  import AppHeader from '@/components/AppHeader'

  export default {
    name: 'app',
    components: {
      NotificationsRequestPopup,
      Loading,
      BottomNav,
      NewVersion,
      MenuSlidein,
      AppHeader,
      BasePricePopup,
    },
    data() {
      return {
        isReloading: false,
        updateAvailable: false,
        serviceWorkerRegistration: false,
        // Handling of ios' homebar which would overlap our menu.
        // Actually this handles the safe area inset bottom, so this might
        // prevent issues on other devices too, if they mess with the available
        // viewport in weird ways.
        hasHomeBar: false,
      }
    },
    mounted() {
      document.addEventListener(
        'serviceWorkerRegistered',
        this.onServiceWorkerRegistered.bind(this),
        { once: true }
      )
      document.addEventListener(
        'serviceWorkerUpdateAvailable',
        this.onServiceWorkerUpdated.bind(this),
        { once: true }
      )

      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.addEventListener('message', (e) => {
          if (e.data.type === 'onpush') {
            this.$store.commit('addNotification', e.data.data)
            switch (e.data.data.type_name) {
              case 'consumer_connected':
              case 'consumer_disconnected':
                this.$store.dispatch('updateCubeConnections')
                break

              case 'price_change':
                // update the price for this connection
                this.$store.dispatch('loadProducerPrices', [
                  {
                    params: {
                      cubeName: e.data.data.triggeringcubename,
                    },
                  },
                ])
            }
          } else if (e.data.type === 'changeUrl') {
            const parts = e.data.data.split('#')
            this.$router.push({
              path: parts[0],
              hash: parts[1],
            })
          }
        })
      }

      // We are doing an autologin, meaning someone needs to log in to the
      // app as a specific user from the crm. The crm redirects to the app url
      // and appends parameters in the format of get params as the url fragment.
      // Example: https://app.efriends.at/#access_token=abcde123&scope=1&...
      // So we need to parse the access token in this case.
      const accessToken = location.hash.match(/access_token=([^&]*)/)
      if (accessToken && accessToken.length > 1 && accessToken[1]) {
        // An access token was found in the url fragment. Use it.
        this.$store.dispatch('doAutologin', accessToken[1])
      }

      // Update authToken and selectedCube according to the values stored in
      // cookies if existing.
      // Retrieve the auth token from the cookie if available.
      const authCookie = this.$store.getters.authCookie

      // Routes that should be usable for non-logged-in users.
      const anonymousRoutes = ['login', 'password-reset', 'new-password']

      if (authCookie) {
        this.$store.commit('setAuthToken', authCookie)
      }

      // We think the user is logged in. If either of the following fails, the
      // cookie is removed in the action so we can show the login form.
      if (this.authCookieIsSet()) {
        // The user seems to be logged in and has a selected a cube. Lets start.
        if (!this.$store.getters.appInitialized) {
          this.$store.dispatch('initializeApp')
        }
      } else {
        this.$store.commit('setIsLoading', false)
        if (anonymousRoutes.indexOf(this.$route.name) < 0) {
          // Redirect the user to the login page if he's not logged in and the
          // requested route is not in the ones allowed for anonymous users.
          this.$router.push({
            name: 'login',
            query: { redirect: this.$route.path },
          })
        }
      }
    },
    computed: {
      appClasses() {
        let classes = ['app']
        if (this.$store.getters.slideinVisible()) {
          classes.push('app--slidein-visible')
        }
        return classes.join(' ')
      },
      layoutClasses() {
        let classes = ['layout', 'layout--default']
        if (this.hasHomeBar) {
          classes.push('layout--has-homebar')
        }
        return classes.join(' ')
      },
    },
    methods: {
      authCookieIsSet: function () {
        return !!Cookie.get(this.$store.getters.authCookieName)
      },
      onServiceWorkerRegistered: function (event) {
        const serviceWorkerRegistration = event.detail
        // Only allow setting up notifications if the browser supports web push.
        if ('pushManager' in serviceWorkerRegistration) {
          this.$store.commit(
            'serviceWorkerRegistered',
            serviceWorkerRegistration
          )
          // We need to wait for the serviceworker to be registered and the
          // getAppConfig request to be finished in order to be able to check any
          // existing web push subscriptions and show the notifications request
          // popup if necessary.
          if (this.$store.getters.appInitialized) {
            this.$store.dispatch('setupPushNotifications')
          }
        }
      },
      onServiceWorkerUpdated: function (event) {
        this.updateAvailable = true
        const serviceWorkerRegistration = event.detail
        serviceWorkerRegistration.waiting.postMessage('updateApp')

        if (this.isReloading) return

        this.isReloading = true
        setTimeout(() => {
          window.location.reload()
        }, 6000)
      },
      closeSlideins() {
        this.$store.commit('hideAllSlideins')
      },
      sidebarCubes: function () {
        return this.$store.getters.allCubesByDistanceFrom({
          filterFunction: (cube) => cube.hasProduction,
          limit: this.$store.getters.mapsSidebarNumCubesShown,
        })
      },
    },
  }
</script>

<style lang="scss">
  @import 'scss/app';

  .app {
    width: 100vw;
    height: 100vh;

    &__version {
      position: absolute;
      bottom: spacer(1);
      left: 50%;
      transform: translateX(-50%);
      opacity: 0.5;
    }
  }

  .layout {
    position: fixed;
    top: 0;
    width: 100%;
    right: 0;
    left: 0;
    bottom: var(--safe-area-inset-bottom);

    display: flex;
    flex-direction: column;

    &__header {
      margin-top: spacer(0.5);
      margin-bottom: spacer(0.25);
      display: flex;
      justify-content: space-between;
      align-items: center;
      transition: $transition-base;
      flex-shrink: 0;
      padding-left: spacer(0.5);
      padding-right: spacer(0.5);

      @media (min-width: $bp-sm) {
        padding-left: spacer(1);
        padding-right: spacer(1);
        margin-top: spacer(1);
        margin-bottom: spacer(1);
      }
    }

    &__content {
      flex-grow: 1;
      overflow-y: auto;
      margin-bottom: $nav-height;
      padding-left: spacer(0.5);
      padding-right: spacer(0.5);
      // To allow content inside to be positioned absolutely, like the history
      // page.
      position: relative;

      @media (min-width: $bp-sm) {
        margin-bottom: $nav-height-sm;
      }
    }
  }

  .logo {
    max-width: $logo-max-width;
    display: block;
    transition: $transition-base;
    margin: spacer(1) auto spacer(2);

    @media (min-height: 260px) {
      margin-top: spacer(3);
      max-width: 200px;
    }

    img {
      max-width: 100%;
      width: 100%;
    }
  }
</style>
